import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output, inject } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { SharedModule } from 'src/shared/shared.module';

declare const bootstrap: any;

export interface DisbursementRevisionDialogResult {
  reason: string;
}

const STATIC_MODAL_OPTIONS = {
  backdrop: 'static',
  keyboard: false,
};
const CLOSE_FALLBACK_MS = 500;

// Shared "ส่งกลับแก้ไข" / "ไม่อนุมัติ" dialog for the disbursement
// certification flow — v1.1 send-backs (teacher/head/finance/vice_dean_admin)
// and the vice_dean_academic revision-review reject are all reason-only, no
// typed revision_type. ConfirmDialogComponent's own DESIGN.md forbids
// embedding form controls inside it, so this component clones its
// imperative open()/close() Bootstrap-modal lifecycle (static backdrop,
// stacked z-index teardown) while hosting a single required reason textarea.
@Component({
  selector: 'app-disbursement-revision-dialog',
  imports: [CommonModule, ReactiveFormsModule, SharedModule],
  templateUrl: './disbursement-revision-dialog.component.html',
  styleUrl: './disbursement-revision-dialog.component.scss',
})
export class DisbursementRevisionDialogComponent {
  @Input() modalId = 'disbursementRevisionDialog';
  @Input() title = 'ส่งกลับแก้ไข';
  @Input() cancelText = 'ยกเลิก';
  @Input() confirmText = 'ยืนยันส่งกลับแก้ไข';
  @Input() isProcessing = false;
  @Input() stackAboveModal = true;

  @Output() submitted = new EventEmitter<DisbursementRevisionDialogResult>();
  @Output() cancelled = new EventEmitter<void>();

  form = inject(FormBuilder).group({
    reason: ['', Validators.required],
  });

  open(): void {
    this.resetForm();

    const modalEl = document.getElementById(this.modalId);
    if (modalEl == null) {
      return;
    }

    bootstrap.Modal.getOrCreateInstance(modalEl, STATIC_MODAL_OPTIONS).show();
  }

  close(): void {
    const modalEl = document.getElementById(this.modalId);
    if (modalEl == null) {
      return;
    }

    this.blurFocusedElementInside(modalEl);

    const shouldRestoreModalOpen = this.stackAboveModal && this.hasAnotherOpenModal(modalEl);
    let completed = false;
    const complete = () => {
      if (completed) {
        return;
      }

      completed = true;
    };

    if (shouldRestoreModalOpen) {
      modalEl.addEventListener('hidden.bs.modal', () => {
        complete();
        queueMicrotask(() => {
          if (this.hasAnotherOpenModal(modalEl)) {
            document.body.classList.add('modal-open');
          }
        });
      }, { once: true });
    } else {
      modalEl.addEventListener('hidden.bs.modal', complete, { once: true });
    }

    bootstrap.Modal.getInstance(modalEl)?.hide();
    setTimeout(() => {
      if (completed) {
        return;
      }

      this.forceHideModalElement(modalEl);
      complete();
    }, CLOSE_FALLBACK_MS);
  }

  onCancel(): void {
    if (this.isProcessing) {
      return;
    }

    this.cancelled.emit();
    this.close();
  }

  onSubmit(): void {
    if (this.isProcessing) {
      return;
    }

    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const { reason } = this.form.getRawValue();
    this.submitted.emit({ reason: String(reason ?? '').trim() });
  }

  private resetForm(): void {
    this.form.reset({ reason: '' });
  }

  private hasAnotherOpenModal(modalEl: HTMLElement): boolean {
    return Array.from(document.querySelectorAll<HTMLElement>('.modal.show'))
      .some((openModal) => openModal !== modalEl);
  }

  private blurFocusedElementInside(modalEl: HTMLElement): void {
    const activeElement = document.activeElement;

    if (activeElement instanceof HTMLElement && modalEl.contains(activeElement)) {
      activeElement.blur();
    }
  }

  private forceHideModalElement(modalEl: HTMLElement): void {
    modalEl.classList.remove('show');
    modalEl.setAttribute('aria-hidden', 'true');
    modalEl.removeAttribute('aria-modal');
    modalEl.removeAttribute('role');
    modalEl.style.display = 'none';

    if (this.hasAnotherOpenModal(modalEl)) {
      document.body.classList.add('modal-open');
      return;
    }

    document.body.classList.remove('modal-open');
    document.body.style.removeProperty('overflow');
    document.body.style.removeProperty('padding-right');
    document.querySelectorAll('.modal-backdrop').forEach((backdrop) => backdrop.remove());
  }
}
